/**
 * WEB-7627 terminal. Primary CTA enters the WEB-7643 delivery-setup tail
 * via CYO without clearing mini-quiz storage. Secondary CTA leaves for the
 * guest discover feed and clears mini-quiz storage — that visitor does not come back.
 *
 * WEB-7833 (Exp 260 guests, including those also in Exp 255): the secondary
 * CTA is dropped, a promo pill with the offer sits above the heading, and the
 * mini-quiz "x" lives in the header (`MiniQuizTerminalHeader`, selected by
 * `MiniQuiz`). A guest in Exp 255 alone is unchanged.
 */
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { fullPageRedirect } from '@hungryroot/environment';
import { useFreemiumReengagementGuest } from '@hungryroot/experiments';
import { FavoriteHeartIcon } from '@hungryroot/pho';
import {
  DISCOVER_FEED_ROUTE,
  ROUTE_MINI_QUIZ_TERMINAL,
} from '@hungryroot/routes';
import { buttonClickedEvent } from '@hungryroot/segment';
import {
  Button,
  CalendarOutline,
  Clock,
  Heading,
  Paragraph,
  Sparkle,
} from '@hungryroot/ui-kit';
import { FreemiumCheckoutButton } from '../../components/FreemiumCheckoutButton/FreemiumCheckoutButton';
import {
  clearQuizLocalStorage,
  MINI_QUIZ_STORAGE_NAMESPACE,
} from '../../defs/localStorage';
import { EXPLORE_HUNGRYROOT_FREE_CTA } from '../../defs/miniQuizCopy';
import { getSignupEnterAnimation } from '../../defs/miniQuizSignupTiming';
import { clearMiniQuizTailSession } from '../../defs/miniQuizTail';
import {
  getFreemiumPromoDeliveryCopy,
  getFreemiumPromoOffer,
  useFreemiumReengagementPromoEffect,
} from '../../hooks/useFreemiumReengagementPromo';
import { useLoadingScreenImages } from '../../hooks/useLoadingScreenDefaults';
import styles from './MiniQuizTerminal.module.css';

const SECONDARY_BUTTON_PROPS = {
  style: 'accent',
  accentColor: 'avocado',
  prominence: 'secondary',
  size: 'medium',
  buttonType: 'button',
} as const;

const BENEFITS = [
  {
    copy: 'No lists or shopping—we handle it',
    Icon: Clock,
  },
  {
    copy: 'A personalized cart, built every week',
    Icon: Sparkle,
  },
  {
    copy: 'Skip, pause or cancel anytime',
    Icon: CalendarOutline,
  },
] as const;

const CARD_POSITION_CLASS = [
  styles['card-left'],
  styles['card-center'],
  styles['card-right'],
] as const;

function heartPopEase(progress: number) {
  return (
    1 -
    Math.exp(-progress * 7.319) *
      (Math.cos(progress * 14.3893) + 0.5086 * Math.sin(progress * 14.3893))
  );
}

const HEART_DELAY_S = 1;
const HEART_POP_S = 0.45;

function FavoriteHeart({
  shouldReduceMotion,
}: {
  shouldReduceMotion: boolean;
}) {
  if (shouldReduceMotion) {
    return (
      <span
        className={styles.heart}
        aria-hidden
        data-testid="mini-quiz-terminal-heart-static"
      >
        <FavoriteHeartIcon isFavorited className={styles['heart-icon']} />
      </span>
    );
  }

  return (
    <motion.span
      className={styles.heart}
      data-testid="mini-quiz-terminal-heart-animated"
      initial={{ opacity: 0, scale: 0.52 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: HEART_DELAY_S,
        duration: HEART_POP_S,
        ease: heartPopEase,
      }}
      aria-hidden
    >
      <FavoriteHeartIcon isFavorited className={styles['heart-icon']} />
    </motion.span>
  );
}

function leaveForDiscoverFeed() {
  clearQuizLocalStorage(MINI_QUIZ_STORAGE_NAMESPACE);
  clearMiniQuizTailSession();
  fullPageRedirect(DISCOVER_FEED_ROUTE);
}

export function MiniQuizTerminal() {
  const images = useLoadingScreenImages(3, MINI_QUIZ_STORAGE_NAMESPACE);
  const shouldReduceMotion = useReducedMotion() ?? false;
  const enterAnimation = getSignupEnterAnimation(shouldReduceMotion);
  const reengagement = useFreemiumReengagementGuest();
  const promoEffect = useFreemiumReengagementPromoEffect();
  const promoOffer = getFreemiumPromoOffer(promoEffect);
  const promoDeliveryCopy = getFreemiumPromoDeliveryCopy(promoEffect);
  // Held back until the audience is known so an Exp 260 guest never sees
  // the Explore CTA flash in and out.
  const showExploreCta =
    !reengagement.isLoading && !reengagement.isReengagementGuest;

  return (
    <motion.div
      className={styles.container}
      data-testid="mini-quiz-terminal"
      initial={enterAnimation.initial}
      animate={enterAnimation.animate}
      transition={enterAnimation.transition}
    >
      <div className={styles.fan}>
        <div className={styles['fan-stack']}>
          {images.map((image, index) => {
            const positionClass = CARD_POSITION_CLASS[index];
            if (positionClass === undefined) {
              return null;
            }

            return (
              <div
                key={image.id}
                className={clsx(styles.card, positionClass)}
                data-testid="mini-quiz-terminal-card"
              >
                <img
                  className={styles['card-image']}
                  src={image.imageUrl}
                  alt={image.name}
                />
                <FavoriteHeart shouldReduceMotion={shouldReduceMotion} />
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.copy}>
        {promoOffer !== null && (
          // H6 type on a span: a callout, not a heading above the page's h1.
          <Heading
            variant="H6"
            as="span"
            className={styles['promo-pill']}
            testid="mini-quiz-terminal-promo-pill"
          >
            {promoOffer}
          </Heading>
        )}
        <Heading variant="HeroAlt" className={styles.heading}>
          Love your picks?
          <br />
          Get them delivered.
        </Heading>
        {promoDeliveryCopy !== null && (
          <Paragraph
            variant="lead"
            className={styles['promo-copy']}
            testid="mini-quiz-terminal-promo-copy"
          >
            {promoDeliveryCopy}
          </Paragraph>
        )}
        <ul className={styles.benefits}>
          {BENEFITS.map(({ copy, Icon }) => (
            <li key={copy} className={styles.benefit}>
              <Icon className={styles['benefit-icon']} />
              <Paragraph variant="body">{copy}</Paragraph>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.actions}>
        {/* The pill above carries the offer here; the button keeps its static copy. */}
        <FreemiumCheckoutButton
          pagePosition="quiz_completion"
          returnPath={ROUTE_MINI_QUIZ_TERMINAL}
          testid="mini-quiz-terminal-deliver"
          showPromoLabel={false}
        />
        {showExploreCta && (
          <Button
            {...SECONDARY_BUTTON_PROPS}
            testid="mini-quiz-terminal-discover"
            className={styles['discover-button']}
            onClick={() => {
              buttonClickedEvent({
                button_name: 'explore_hungryroot_free',
                page_position: 'quiz_completion',
                order_id: null,
              });
              leaveForDiscoverFeed();
            }}
          >
            {EXPLORE_HUNGRYROOT_FREE_CTA}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
